// src/pages/MissionDetailPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  MapPin,
  User,
  Phone,
  Navigation,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  Copy,
  ExternalLink,
  Zap,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'react-toastify';

// Components
import DeliveryMap from '../components/map/DeliveryMap';
import QrScannerModal from '../components/common/QrScannerModal';
import AppLogo from '../components/common/AppLogo';

// Store
import {
  fetchDeliveryById,
  updateDeliveryStatus,
  selectCurrentDelivery,
  selectDriverProfile
} from '../store/slices/driverSlice';

// Hook
import useGeolocation from '../hooks/useGeolocation';

const MissionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [failureReason, setFailureReason] = useState('');

  // Selectors
  const mission = useSelector(selectCurrentDelivery);
  const profile = useSelector(selectDriverProfile);

  // GPS Hook
  const { position } = useGeolocation({
    enableHighAccuracy: true,
    watchPosition: true
  });

  // Load mission data
  useEffect(() => {
    loadMission();
  }, [id]);

  const loadMission = async () => {
    setLoading(true);
    try {
      await dispatch(fetchDeliveryById(id)).unwrap();
    } catch (error) {
      console.error('Error loading mission:', error);
      toast.error('Erreur lors du chargement de la mission');
      navigate('/driver/missions');
    } finally {
      setLoading(false);
    }
  };

  const handleQrScanned = async (confirmationToken) => {
    setShowQrScanner(false);
    setActionLoading(true);
    try {
      await dispatch(updateDeliveryStatus({ deliveryId: id, status: 'delivered', confirmationToken })).unwrap();
      toast.success('Mission terminee!');
      navigate('/driver');
    } catch (error) {
      toast.error(error?.message || 'Erreur lors de la completion — vérifiez le QR code du client');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFail = async () => {
    if (!failureReason.trim()) {
      toast.warning('Veuillez indiquer la raison');
      return;
    }

    setActionLoading(true);
    try {
      await dispatch(updateDeliveryStatus({
        deliveryId: id,
        status: 'failed_delivery',
        reason: failureReason
      })).unwrap();
      toast.info('Mission marquee comme echouee');
      setShowFailModal(false);
      navigate('/driver');
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
    } finally {
      setActionLoading(false);
    }
  };

  const openNavigation = () => {
    if (!mission) return;
    const lat = mission.delivery_latitude || mission.to_latitude;
    const lng = mission.delivery_longitude || mission.to_longitude;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    toast.success('Adresse copiee');
  };

  const callRecipient = () => {
    if (mission?.recipient_phone) {
      window.open(`tel:${mission.recipient_phone}`);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { label: 'En attente', color: 'orange', icon: Clock },
      processing: { label: 'En preparation', color: 'blue', icon: Package },
      shipped: { label: 'Expediee', color: 'blue', icon: Package },
      in_transit: { label: 'En transit', color: 'blue', icon: Navigation },
      out_for_delivery: { label: 'En livraison', color: 'emerald', icon: Navigation },
      delivered: { label: 'Livree', color: 'emerald', icon: CheckCircle },
      failed_delivery: { label: 'Echec', color: 'red', icon: XCircle }
    };
    return statusMap[status] || { label: status, color: 'gray', icon: Package };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement de la mission...</p>
        </div>
      </div>
    );
  }

  if (!mission) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Mission non trouvee</p>
          <button
            onClick={() => navigate('/driver/missions')}
            className="mt-4 px-4 py-2 bg-emerald-500 text-white rounded-xl"
          >
            Retour aux missions
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(mission.status);
  const StatusIcon = statusInfo.icon;
  const isUrgent = mission.is_urgent || mission.method === 'express';
  const isCompleted = ['delivered', 'failed_delivery'].includes(mission.status);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/50">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(-1)}
              className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </motion.button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">
                {mission.order_number || `#${mission.tracking_number?.slice(-8) || mission.id?.slice(-8)}`}
              </h1>
              {mission.order_number && mission.tracking_number && (
                <p className="text-xs text-gray-400 -mt-0.5">{mission.tracking_number}</p>
              )}
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white bg-${statusInfo.color}-500`}>
                  <StatusIcon className="w-3 h-3" />
                  {statusInfo.label}
                </span>
                {isUrgent && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    <Zap className="w-3 h-3" />
                    Express
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xl font-bold text-emerald-600">
                {(mission.cost || mission.driver_fee || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500">FCFA</p>
            </div>
            <AppLogo size={32} />
          </div>
        </div>
      </header>

      {/* Map Section */}
      <div className="h-64 relative">
        <DeliveryMap
          driverPosition={position ? [position.latitude, position.longitude] : null}
          currentDelivery={mission}
          className="h-full w-full"
        />
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={openNavigation}
          className="absolute bottom-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-xl flex items-center gap-2 shadow-lg"
        >
          <Navigation className="w-5 h-5" />
          Naviguer
        </motion.button>
      </div>

      {/* Content */}
      <main className="px-4 py-4 pb-32">
        {/* Addresses Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-4 mb-4 shadow-sm"
        >
          <h2 className="text-sm font-semibold text-gray-500 mb-3">ITINERAIRE</h2>

          {/* Pickup Address */}
          {mission.from_address && (
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Recuperation</p>
                <p className="text-sm font-medium text-gray-900">{mission.from_address}</p>
              </div>
              <button
                onClick={() => copyAddress(mission.from_address)}
                className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center"
              >
                <Copy className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          )}

          {/* Connector */}
          {mission.from_address && mission.to_address && (
            <div className="flex items-center gap-3 mb-4 pl-5">
              <div className="w-0.5 h-8 bg-gray-200 rounded-full" />
            </div>
          )}

          {/* Delivery Address */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500">Livraison</p>
              <p className="text-sm font-medium text-gray-900">
                {mission.to_address || 'Adresse non specifiee'}
              </p>
            </div>
            <button
              onClick={() => copyAddress(mission.to_address)}
              className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center"
            >
              <Copy className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </motion.div>

        {/* Recipient Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-4 mb-4 shadow-sm"
        >
          <h2 className="text-sm font-semibold text-gray-500 mb-3">DESTINATAIRE</h2>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
              <User className="w-6 h-6 text-gray-500" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">{mission.recipient_name || 'Client'}</p>
              {mission.recipient_phone && (
                <p className="text-sm text-gray-500">{mission.recipient_phone}</p>
              )}
            </div>
            {mission.recipient_phone && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={callRecipient}
                className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center"
              >
                <Phone className="w-5 h-5 text-emerald-600" />
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Delivery Instructions */}
        {mission.delivery_instructions && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4"
          >
            <div className="flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800 mb-1">Instructions de livraison</p>
                <p className="text-sm text-amber-700">{mission.delivery_instructions}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Order Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <h2 className="text-sm font-semibold text-gray-500 mb-3">DETAILS</h2>

          <div className="space-y-3">
            {mission.method && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Methode</span>
                <span className="text-sm font-medium text-gray-900 capitalize">{mission.method}</span>
              </div>
            )}

            {mission.estimated_delivery_time && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Temps estime</span>
                <span className="text-sm font-medium text-gray-900">{mission.estimated_delivery_time} min</span>
              </div>
            )}

            {mission.distance && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Distance</span>
                <span className="text-sm font-medium text-gray-900">{mission.distance} km</span>
              </div>
            )}

            {mission.delivery_zone && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Zone</span>
                <span className="text-sm font-medium text-gray-900">{mission.delivery_zone}</span>
              </div>
            )}

            <div className="flex justify-between items-center pt-3 border-t border-gray-100">
              <span className="text-sm font-medium text-gray-700">Commission</span>
              <span className="text-lg font-bold text-emerald-600">
                {(mission.driver_fee || mission.cost || 0).toLocaleString()} FCFA
              </span>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Bottom Actions — les missions n'apparaissent ici que déjà assignées
          par l'admin (livreurs internes), donc directement actionnables. */}
      {!isCompleted && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-t border-gray-200/50 p-4 z-40">
          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowFailModal(true)}
              className="flex-1 py-4 bg-red-100 text-red-700 font-semibold rounded-xl"
            >
              Echec
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowCompleteModal(true)}
              disabled={actionLoading}
              className="flex-1 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-xl shadow-lg shadow-emerald-200"
            >
              Marquer comme livre
            </motion.button>
          </div>
        </div>
      )}

      {/* Complete Confirmation Modal */}
      <AnimatePresence>
        {showCompleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center"
            onClick={() => setShowCompleteModal(false)}
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-t-3xl md:rounded-3xl p-6 w-full md:max-w-md"
            >
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                Confirmer la livraison
              </h3>
              <p className="text-gray-500 text-center mb-6">
                Demandez au client d'afficher son QR code de livraison, puis scannez-le pour valider — c'est obligatoire, ça protège aussi bien le client que vous en cas de litige.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCompleteModal(false)}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl"
                >
                  Annuler
                </button>
                <button
                  onClick={() => { setShowCompleteModal(false); setShowQrScanner(true); }}
                  disabled={actionLoading}
                  className="flex-1 py-3 bg-emerald-500 text-white font-semibold rounded-xl disabled:opacity-50"
                >
                  Scanner le QR code
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* QR Scanner — confirmation obligatoire de la remise */}
      <AnimatePresence>
        {showQrScanner && (
          <QrScannerModal
            onScan={handleQrScanned}
            onClose={() => setShowQrScanner(false)}
          />
        )}
      </AnimatePresence>

      {/* Failure Modal */}
      <AnimatePresence>
        {showFailModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center"
            onClick={() => setShowFailModal(false)}
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-t-3xl md:rounded-3xl p-6 w-full md:max-w-md"
            >
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                Echec de livraison
              </h3>
              <p className="text-gray-500 text-center mb-4">
                Indiquez la raison de l'echec
              </p>
              <textarea
                value={failureReason}
                onChange={(e) => setFailureReason(e.target.value)}
                placeholder="Ex: Client absent, adresse incorrecte..."
                className="w-full p-3 border border-gray-200 rounded-xl mb-4 resize-none h-24 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFailModal(false)}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl"
                >
                  Annuler
                </button>
                <button
                  onClick={handleFail}
                  disabled={actionLoading}
                  className="flex-1 py-3 bg-red-500 text-white font-semibold rounded-xl disabled:opacity-50"
                >
                  {actionLoading ? 'Chargement...' : 'Confirmer echec'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MissionDetailPage;
